import { ClearBaseIcon } from '@tableau/icons/ClearBaseIcon';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { ClickBustingConfig } from '../../config/ClickBusting';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';

// unfortunately exported types cannot be template literal types (a new feature of typescript 4.1) until all of our consumers have upgraded typescript version
type ChipColorNames = '--chip-background-default' | '--chip-background-disabled' | '--chip-background-hover' | '--chip-background-press'
  | '--chip-content-default' | '--chip-content-disabled' | '--chip-content-hover' | '--chip-content-press';
export type ChipCSSVariables = { [key in ChipColorNames]: string };

const DefaultChipCSSVariables: ChipCSSVariables = Object.freeze({
  '--chip-background-default': '#E5E5E5',
  '--chip-background-disabled': 'transparent',
  '--chip-background-hover': '#C9C9C9',
  '--chip-background-press': '#AEAEAE',
  '--chip-content-default': '#5C5C5C',
  '--chip-content-disabled': '#C9C9C9',
  '--chip-content-hover': '#444',
  '--chip-content-press': '#2E2E2E',
});

const ChipStyleName = TypeStyle.style(PreventUserSelect, {
  ...DefaultChipCSSVariables,
  alignItems: 'center',
  backgroundColor: 'var(--chip-background-default)',
  border: '1px solid var(--chip-content-default)',
  borderRadius: 1,
  boxSizing: 'border-box',
  color: 'var(--chip-content-default)',
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: TabStyles.Typography.Sizing.Default,
  lineHeight: 'inherit',
  overflow: 'hidden',
  padding: '0 3px',
  position: 'relative',
  transition: 'border-color 75ms',
  $nest: {
    '&:hover': {
      backgroundColor: 'var(--chip-background-hover)',
      color: 'var(--chip-content-hover)',
    },
    // note that aria-selected is unused because multi selection is not supported yet
    '&[aria-selected="true"]': { backgroundColor: '#cccccc' },
    '&:focus': { // not focus-visible because it's a subelement of a conceptual <input>, which always show focus
      ...DefaultFocusIndicator(),
      // need visual hierarchy distinction between the chip container and the chip itself when focused
      outlineWidth: 1,
      outlineOffset: -2,
    },
    '&:active': {
      backgroundColor: 'var(--chip-background-press)',
      color: 'var(--chip-content-press)',
    },
    '&[aria-disabled="true"]': {
      backgroundColor: 'var(--chip-background-disabled) !important',
      borderColor: 'var(--chip-content-disabled) !important',
      color: 'var(--chip-content-disabled) !important',
    },
    '&.pending::after': {
      backgroundColor: 'rgba(255,255,255,0.6)',
      bottom: 0,
      content: `""`,
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 0,
    }
  }
});

export interface ChipProps {
  /** The rendered contents of this chip. */
  children: React.ReactNode;

  /** Show a Remove button that invokes this callback, if provided. */
  onRemove?: () => void;

  /**
   * Renders the chip with a "pending" visual style.
   * Typically used to indicate some sort of asynchronous validation is occurring.
   */
  pending?: boolean;

  testId?: string;

  /**
   * The tooltip of the chip (excluding the Remove button). n.b. implementation details of the tooltip are subject to change;
   * the contract of this prop is only that a tooltip will somehow be displayed.
   */
  tooltip?: string;

  /** The unique value of this chip. */
  value: string;
}

const RemoveButtonStyleName = TypeStyle.style({
  flexShrink: 0,
  marginLeft: 5,
  marginRight: 2,
  $nest: {
    '&:hover': { color: 'var(--chip-content-hover)' },
    '&:active': { color: 'var(--chip-content-press)' }
  }
});

const RemoveButton: React.FunctionComponent<Pick<ChipProps, 'onRemove'>> = props => <ClearBaseIcon
  alt={Messages.remove()}
  aria-hidden={true}
  className={RemoveButtonStyleName}
  data-tb-test-id='remove-Button'
  onClick={props.onRemove && (e => { props.onRemove?.(); e.stopPropagation(); })}
  role='button'
  size={12}
/>;

export const Chip = React.forwardRef<HTMLDivElement, ChipProps & React.HTMLAttributes<HTMLElement>>((props, ref) => {
  const { className, children, onRemove, pending, testId, tooltip, value, ...htmlAttrs } = props;

  return (
    <div
      data-tb-test-id={makeTestId(testId, 'Chip')}
      ref={ref}
      title={tooltip} // can be overridden by setting `title` prop
      {...htmlAttrs}
      className={TypeStyle.classes(ChipStyleName, ClickBustingConfig.suppressionClassName, { pending }, className)}
    >
      {children}
      {onRemove && <RemoveButton onRemove={onRemove} />}
    </div>
  );
});
Chip.displayName = 'Chip';
